/**
 * Gebruikers en toegang (ontwerp v4 §2, achter het profielblok).
 *
 * De mock noemt drie rollen — meekijken, beantwoorden, beheer — en de
 * database noemt er drie andere: alleen_lezen, medewerker, admin. JP
 * heeft gekozen (antwoord 7): de databasenamen blijven staan, we
 * vertalen alleen bij het tonen. Dit scherm laat allebei zien, zodat
 * niemand hoeft te raden welk woord waar hoort.
 *
 * Uitnodigen bestaat nog niet. De knop staat er wel, uitgeschakeld en
 * met de reden erbij: een knop die stil niets doet is erger dan geen
 * knop.
 *
 * Sessie 77: een vierde rol, toets. Zo'n rij krijgt de chip "Toets" en
 * een knop om het account uit of weer aan te zetten. De knop staat onder
 * de naam, zodat hij ook op 390 px in beeld blijft.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { gebruikersApi, vertaalApiFout } from "@/lib/api";
import {
  werkplekKlantenApi,
  type WerkplekToegangLijst,
  type WerkplekToegangRegel,
} from "@/lib/werkplek-api";

import { datumKort } from "../Klanten/opmaak";
import "../Klanten/klantpagina.css";
import { MetCopiloot } from "../Copiloot/MetCopiloot";

const TOEGANG_SLEUTEL = ["werkplek", "toegang"] as const;

/**
 * In de v6-schil wint de reset `.v6 button` van `.wp-knop` (hogere
 * specificiteit), waardoor de knop als kale tekst van 19 px hoog op de
 * iPhone stond. Hier lokaal de knopvorm en een tikvlak van 32 px.
 */
const KNOP_STIJL = {
  border: "1px solid var(--wp-lijn)",
  background: "var(--wp-paneel)",
  borderRadius: 7,
  padding: "6px 12px",
  minHeight: 32,
  marginTop: 6,
} as const;

const REDEN_GEEN_BEHEER = "Alleen iemand met Beheer kan dit account uitzetten.";

/**
 * Uit- of weer aanzetten van een toetsaccount. Geen bevestigingsvraag:
 * de knop is omkeerbaar met dezelfde knop.
 */
function ToetsSchakelaar({
  gebruiker,
  magBeheren,
  redenUit,
}: {
  gebruiker: WerkplekToegangRegel;
  magBeheren: boolean;
  redenUit: string | null;
}) {
  const qc = useQueryClient();
  const schakel = useMutation({
    mutationFn: async (): Promise<void> => {
      if (gebruiker.actief) {
        await gebruikersApi.deactiveer(gebruiker.id);
      } else {
        await gebruikersApi.wijzig(gebruiker.id, { actief: true });
      }
    },
    onSuccess: () => {
      toast.success(
        gebruiker.actief ? "Toetsaccount uitgezet" : "Toetsaccount aangezet",
      );
      // Eerst de nieuwe stand zelf tonen: een meteen opnieuw opgehaalde
      // lijst kan de wijziging nog net missen, en dan bleef de oude knop staan.
      qc.setQueryData<WerkplekToegangLijst>(TOEGANG_SLEUTEL, (oud) =>
        oud
          ? {
              ...oud,
              gebruikers: oud.gebruikers.map((g) =>
                g.id === gebruiker.id ? { ...g, actief: !gebruiker.actief } : g,
              ),
            }
          : oud,
      );
      void qc.invalidateQueries({ queryKey: TOEGANG_SLEUTEL });
    },
    onError: (fout) => {
      toast.error(
        vertaalApiFout(fout, "Het toetsaccount kon niet worden aangepast."),
      );
    },
  });

  return (
    <button
      type="button"
      className="wp-knop wp-knop-klein"
      style={KNOP_STIJL}
      disabled={!magBeheren || schakel.isPending}
      title={magBeheren ? undefined : (redenUit ?? REDEN_GEEN_BEHEER)}
      onClick={() => schakel.mutate()}
    >
      {gebruiker.actief ? "Uitzetten" : "Weer aanzetten"}
    </button>
  );
}

export function WerkplekToegang() {
  const toegang = useQuery({
    queryKey: TOEGANG_SLEUTEL,
    queryFn: () => werkplekKlantenApi.toegang(),
  });

  return (
    <>
      <div className="wp-bovenbalk">
        <h1 style={{ fontSize: 17, margin: 0, fontWeight: 600 }}>
          Gebruikers en toegang
        </h1>
      </div>

      <div className="wp-inhoud">
        <MetCopiloot pagina="beheer">
        {/* Onder 900 px verbergt de gedeelde CSS elke .wp-kolom zonder
            data-mob-aan="ja"; de tabs van MetCopiloot doen hier het wisselen. */}
        <div className="wp-kolom" data-mob-aan="ja" style={{ borderRight: 0 }}>
          <section className="wp-blok">
            <h4>Wie kan wat</h4>

            {toegang.isLoading ? (
              <p className="wp-stil">Gebruikers worden geladen.</p>
            ) : toegang.isError ? (
              <p className="wp-stil" role="alert">
                De gebruikers konden niet geladen worden.{" "}
                {vertaalApiFout(toegang.error)}
              </p>
            ) : (
              <>
                {/* Op een smal scherm scrolt de tabel binnen zijn blok,
                    niet de hele pagina. */}
                <div style={{ overflowX: "auto" }}>
                <table className="wp-tabel">
                  <thead>
                    <tr>
                      <th>Naam</th>
                      <th>E-mail</th>
                      <th>Toegang</th>
                      <th>Rol in de database</th>
                      <th>Laatst ingelogd</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(toegang.data?.gebruikers ?? []).map((gebruiker) => (
                      <tr key={gebruiker.id}>
                        <td>
                          {gebruiker.naam}
                          {gebruiker.rol === "toets" ? (
                            <span className="wp-chip wp-chip-grijs" style={{ marginLeft: 6 }}>
                              Toets
                            </span>
                          ) : null}
                          {gebruiker.actief ? null : (
                            <span className="wp-chip wp-chip-grijs" style={{ marginLeft: 6 }}>
                              niet actief
                            </span>
                          )}
                          {gebruiker.rol === "toets" ? (
                            <div style={{ marginTop: 6 }}>
                              <ToetsSchakelaar
                                gebruiker={gebruiker}
                                magBeheren={toegang.data?.mag_beheren ?? false}
                                redenUit={toegang.data?.reden_uit ?? null}
                              />
                            </div>
                          ) : null}
                        </td>
                        <td>{gebruiker.email}</td>
                        <td title={gebruiker.toegang_label}>
                          {gebruiker.toegang}
                        </td>
                        {/* De databasenaam blijft staan: alleen het
                            tonen vertaalt, niet de opslag. */}
                        <td className="wp-stil">{gebruiker.rol}</td>
                        <td>
                          {gebruiker.laatst_ingelogd_op
                            ? datumKort(gebruiker.laatst_ingelogd_op)
                            : "nog nooit"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>

                <p className="wp-stil">
                  {toegang.data?.teller ?? 0}{" "}
                  {(toegang.data?.teller ?? 0) === 1
                    ? "gebruiker"
                    : "gebruikers"}
                </p>
              </>
            )}
          </section>

          <section className="wp-blok">
            <h4>Iemand toevoegen</h4>
            <div className="wp-knoprij">
              <button
                type="button"
                className="wp-knop"
                disabled
                title="Uitnodigen per mail is er nog niet."
              >
                Gebruiker uitnodigen
              </button>
              <span className="wp-stil">
                Uitnodigen per mail bestaat nog niet. Een nieuwe gebruiker
                maak je voorlopig aan op het bestaande beheerscherm.
              </span>
            </div>
            {toegang.data && !toegang.data.mag_beheren ? (
              <p className="wp-stil">{toegang.data.reden_uit}</p>
            ) : null}
          </section>

          <section className="wp-blok">
            <h4>Wat de vier woorden betekenen</h4>
            {/* Eigen klasse (sessie 46, B10): `wp-feiten` is het raster van
                de zaakfeiten, met een eerste kolom van 104 px. Daarin
                stonden deze regels in een smalle strook. */}
            <ul className="wp-woorden">
              <li>
                <b>Meekijken</b>: leest mee, verstuurt niets.
              </li>
              <li>
                <b>Beantwoorden</b>: behandelt zaken en verstuurt
                antwoorden.
              </li>
              <li>
                <b>Beheer</b>: alles, inclusief instellingen en
                gebruikers.
              </li>
              <li>
                <b>Toets</b>: ziet alles wat Beantwoorden ziet, schrijft
                alleen op proefaccounts, blijft ingelogd.
              </li>
            </ul>
          </section>
        </div>
      </MetCopiloot>
      </div>
    </>
  );
}
